import React, { useEffect, useState } from 'react';
import axios from 'axios';
import NumberInput from '../../shared/NumberInput';
import { formatNumber } from '../../shared/numberFormat';
import { useShipmentWeightUnit, useShipmentDimensionUnit, shipmentWeightLabel, shipmentDimensionLabel } from '../../shared/useShipmentWeightUnit';
import { WEIGHT_TO_KG } from './packageShared';
import '../../shared/StyledInput.css';

const fieldLabelStyle = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 };
const fieldWrapStyle = { marginBottom: 14 };

const DIMENSIONS = [
  { key: 'length', placeholder: 'L', title: 'Length' },
  { key: 'width', placeholder: 'W', title: 'Width' },
  { key: 'height', placeholder: 'H', title: 'Height' },
];

// One package's Weight, Dimensions and Packaging (issue #299) — none
// required. Packaging comes from the tenant's Packaging list (Warehouse ›
// Packaging); picking one fills in its dimensions, which can then be
// overridden. Shared by Pick Order's package editor and the Ship dialog.
// Controlled: `value` is { weight, length, width, height, packaging_id }
// as strings (packageShared.detailsDraft); `onChange(next)` gets the whole
// draft back.
function PackageDetailsFields({ value, onChange }) {
  const weightUnit = useShipmentWeightUnit();
  const dimensionUnit = useShipmentDimensionUnit();
  const [packagings, setPackagings] = useState([]);

  useEffect(() => {
    axios.get('/api/packagings')
      .then((res) => setPackagings(res.data.data || []))
      .catch(() => setPackagings([]));
  }, []);

  const setField = (key, val) => onChange({ ...value, [key]: val });

  const pickPackaging = (id) => {
    const packaging = packagings.find((p) => String(p.id) === String(id));
    if (!packaging) return onChange({ ...value, packaging_id: '' });
    const str = (v) => (v === null || v === undefined ? '' : String(v));
    // A Satchel's Flat Size is for reference only, so it fills nothing in.
    if (packaging.packaging_type === 'satchel') return onChange({ ...value, packaging_id: String(packaging.id) });
    return onChange({
      ...value,
      packaging_id: String(packaging.id),
      length: packaging.length != null ? str(packaging.length) : value.length,
      width: packaging.width != null ? str(packaging.width) : value.width,
      height: packaging.height != null ? str(packaging.height) : value.height,
    });
  };

  // A package already recorded with Packaging since retired still shows it.
  const listed = packagings.some((p) => String(p.id) === String(value.packaging_id));

  // A weight over the picked Packaging's Maximum Weight (issue #314) gets a
  // soft warning (ui-architecture.md's Soft Warning, Not Hard Block).
  const picked = packagings.find((p) => String(p.id) === String(value.packaging_id));
  const overMax = !!picked && picked.max_weight != null && value.weight !== '' && value.weight != null
    && Number(value.weight) > Number(picked.max_weight);
  // A flat-rate product over its carrier's limit doesn't qualify for the
  // flat rate (issue #316) — said here, before Ship, as a soft warning too.
  // A flat-rate product has no Maximum Weight of its own, so only one shows.
  const weightKg = value.weight !== '' && value.weight != null ? Number(value.weight) * (WEIGHT_TO_KG[weightUnit] ?? 1) : null;
  const flatMaxKg = picked?.flat_rate_max_kg ?? null;
  const overFlatRate = flatMaxKg !== null && weightKg !== null && weightKg > flatMaxKg;

  return (
    <>
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Packaging</label>
        <select
          className="styled-input"
          value={value.packaging_id || ''}
          onChange={(e) => pickPackaging(e.target.value)}
          style={{ width: '100%', fontSize: 14 }}
        >
          <option value="">—</option>
          {value.packaging_id && !listed && <option value={value.packaging_id}>(retired)</option>}
          {packagings.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>{shipmentDimensionLabel(dimensionUnit)}</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {DIMENSIONS.map(({ key, placeholder, title }) => (
            <NumberInput
              key={key}
              decimals={3}
              step="0.001"
              min={0}
              placeholder={placeholder}
              title={title}
              value={value[key] ?? ''}
              onChange={(e) => setField(key, e.target.value)}
              style={{ flex: 1, minWidth: 0, fontSize: 14 }}
            />
          ))}
        </div>
      </div>
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>{shipmentWeightLabel(weightUnit)}</label>
        <NumberInput
          decimals={3}
          step="0.001"
          min={0}
          placeholder={shipmentWeightLabel(weightUnit)}
          value={value.weight ?? ''}
          onChange={(e) => setField('weight', e.target.value)}
          style={{ width: '100%', fontSize: 14 }}
        />
        {overMax && (
          <div style={{ fontSize: 12, color: '#7a5c00', background: '#fff8e1', border: '1px solid #f0c34d', borderRadius: 6, padding: '6px 10px', marginTop: 6 }}>
            This is over {picked.name}’s Maximum Weight of {formatNumber(picked.max_weight, { maxDecimals: 3 })}{weightUnit ? ` ${weightUnit}` : ''}. Check the weight — you can still save it.
          </div>
        )}
        {overFlatRate && (
          <div style={{ fontSize: 12, color: '#7a5c00', background: '#fff8e1', border: '1px solid #f0c34d', borderRadius: 6, padding: '6px 10px', marginTop: 6 }}>
            This is over {formatNumber(flatMaxKg, { maxDecimals: 3 })} kg, {picked.carrier?.name || 'the carrier'}’s limit for {picked.flat_rate_product_name || picked.name}. It won’t qualify for the flat rate.
          </div>
        )}
      </div>
    </>
  );
}

export default PackageDetailsFields;
